import { FileUpload, Select, TextArea, TextField } from "@/design-system";
import { ticketService, type TicketInput } from "@/services";
import { useToast } from "@/hooks";
import {
  CRM_ATTACHMENT_ACCEPT,
  CRM_ATTACHMENT_MAX_BYTES,
  crmAttachmentSizeError,
} from "@/utils";
import { useState } from "react";
import { TicketAttachmentList } from "./TicketAttachmentList";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

export const NEW_TICKET_FORM_ID = "new-ticket-form";

const MAX_ATTACHMENTS = 5;

const schema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Conte em poucas palavras o que houve.")
    .max(120, "Máximo de 120 caracteres."),
  labelId: z.string(),
  description: z.string().trim().max(2000, "Máximo de 2000 caracteres."),
});

type FormValues = z.infer<typeof schema>;

type NewTicketFormProps = {
  onSubmit: (input: TicketInput, attachments: File[]) => void;
  /** Sending: fields and files are locked so nothing changes unseen. */
  disabled?: boolean;
};

/**
 * New-ticket fields. The submit button lives in the modal footer and points
 * at this form through `NEW_TICKET_FORM_ID`.
 */
export const NewTicketForm = ({
  onSubmit,
  disabled = false,
}: NewTicketFormProps) => {
  const [attachments, setAttachments] = useState<File[]>([]);
  const { showToast } = useToast();

  // FileUpload only knows the 50 MB video ceiling; other types stop at 10 MB.
  const addAttachment = (file: File) => {
    const error = crmAttachmentSizeError(file);

    if (error) {
      showToast(error, "error");
      return;
    }

    setAttachments(current => [...current, file]);
  };

  const { data: labels } = useQuery({
    queryKey: ["tickets", "labels"],
    queryFn: ticketService.listLabels,
    staleTime: 5 * 60_000,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: "", labelId: "", description: "" },
  });

  const submit = (values: FormValues) =>
    onSubmit(
      {
        title: values.title,
        description: values.description === "" ? null : values.description,
        label_id: values.labelId === "" ? null : values.labelId,
      },
      attachments
    );

  return (
    <form id={NEW_TICKET_FORM_ID} onSubmit={handleSubmit(submit)} noValidate>
      <fieldset disabled={disabled} className="flex flex-col gap-4">
        <TextField
          label="Título"
          required
          maxLength={120}
          error={errors.title?.message}
          {...register("title")}
        />
        <Controller
          control={control}
          name="labelId"
          render={({ field }) => (
            <Select
              label="Tipo"
              options={(labels ?? []).map(label => ({
                value: label.id,
                label: label.name,
              }))}
              value={field.value}
              onValueChange={field.onChange}
              placeholder="Escolha o tipo"
              allowDeselect
            />
          )}
        />
        <TextArea
          label="Descrição"
          maxLength={2000}
          rows={6}
          hint="Onde aconteceu, o que você esperava e o que apareceu."
          error={errors.description?.message}
          {...register("description")}
        />
        {attachments.length < MAX_ATTACHMENTS && (
          <FileUpload
            label="Anexos"
            hint="Print, vídeo da tela ou arquivo. Vídeo até 50 MB; o resto até 10 MB. Até 5 anexos."
            accept={CRM_ATTACHMENT_ACCEPT}
            maxSize={CRM_ATTACHMENT_MAX_BYTES}
            file={null}
            showPreview={false}
            triggerLabel="Anexar arquivo"
            isUploading={disabled}
            onSelect={addAttachment}
          />
        )}
        <TicketAttachmentList
          files={attachments}
          disabled={disabled}
          onRemove={index =>
            setAttachments(current => current.filter((_, i) => i !== index))
          }
        />
      </fieldset>
    </form>
  );
};
